import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Save, Search, Package } from "lucide-react";
import { salesAPI, customersAPI, productsAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { FieldError } from "@/components/ui/field-error";

interface SalesItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	barcode?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface SalesForm {
	customerId: string;
	saleDate: string;
	status: "pending" | "ordered" | "received";
	paymentStatus: "unpaid" | "partial" | "paid";
	discountAmount: number;
	taxAmount: number;
	shippingCost: number;
	notes: string;
	items: SalesItem[];
}

interface SaleUpdatePayload {
	customerId?: number;
	status?: "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
	paymentStatus?: "pending" | "partial" | "paid";
	subtotal?: number;
	taxAmount?: number;
	discountAmount?: number;
	shippingAmount?: number;
	totalAmount?: number;
	notes?: string;
	items: Array<{
		productId: number;
		quantity: number;
		unitPrice: number;
		discountRate?: number;
		taxRate?: number;
	}>;
}

const InvoiceEditPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [customers, setCustomers] = useState<any[]>([]);
	const [products, setProducts] = useState<any[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<SalesItem[]>([]);
	const [salesForm, setSalesForm] = useState<SalesForm>({
		customerId: "",
		saleDate: new Date().toISOString().split("T")[0],
		status: "pending",
		paymentStatus: "unpaid",
		discountAmount: 0,
		taxAmount: 0,
		shippingCost: 0,
		notes: "",
		items: [],
	});

	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const { isDirty } = useIsDirty({ salesForm, selectedProducts }, !loading);
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	const clearFieldError = (field: string) => {
		setFieldErrors(prev => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		if (id) {
			fetchInvoice();
			fetchCustomers();
			fetchProducts();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredProducts(products);
		} else {
			const filtered = products.filter(
				product =>
					product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					product.barcode?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
			setFilteredProducts(filtered);
		}
	}, [searchQuery, products]);

	const fetchInvoice = async () => {
		try {
			setLoading(true);
			const data = await salesAPI.getById(id!);
			setSalesForm({
				customerId: data.customerId || "",
				saleDate: data.saleDate || new Date().toISOString().split("T")[0],
				status: data.status || "pending",
				paymentStatus: data.paymentStatus || "unpaid",
				discountAmount: data.discountAmount || 0,
				taxAmount: data.taxAmount || 0,
				shippingCost: data.shippingAmount || 0,
				notes: data.notes || "",
				items: [],
			});
			setSelectedProducts(data.items || []);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("invoices.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/invoices");
		} finally {
			setLoading(false);
		}
	};

	const fetchCustomers = async () => {
		try {
			const data = await customersAPI.getAll({ page: 1, limit: 1000 });
			setCustomers(data.data || []);
		} catch (error) {
			console.error("Failed to fetch customers:", error);
		}
	};

	const fetchProducts = async () => {
		try {
			const data = await productsAPI.getAll({ page: 1, limit: 1000 });
			setProducts(data.data || []);
			setFilteredProducts(data.data || []);
		} catch (error) {
			console.error("Failed to fetch products:", error);
		}
	};

	const handleInputChange = (field: keyof SalesForm, value: any) => {
		setSalesForm(prev => ({ ...prev, [field]: value }));
		clearFieldError(field);
	};

	// Unified line math (matches the backend): discount = subtotal*discountRate/100;
	// tax = (subtotal - discount)*taxRate/100; total = subtotal - discount + tax.
	const recalcItem = (item: SalesItem): SalesItem => {
		const lineSubtotal = item.unitPrice * item.quantity;
		const discountAmount = (lineSubtotal * item.discountPercentage) / 100;
		const taxAmount = ((lineSubtotal - discountAmount) * item.taxPercentage) / 100;
		const totalAmount = lineSubtotal - discountAmount + taxAmount;
		return { ...item, discountAmount, taxAmount, totalAmount };
	};

	const addProductToSale = (product: any) => {
		clearFieldError("items");
		const existingItem = selectedProducts.find(item => item.productId === product.id);

		if (existingItem) {
			setSelectedProducts(prev =>
				prev.map(item =>
					item.productId === product.id ? recalcItem({ ...item, quantity: item.quantity + 1 }) : item,
				),
			);
		} else {
			const newItem: SalesItem = recalcItem({
				id: Date.now().toString(),
				productId: product.id,
				productName: product.name,
				productImage: product.image,
				barcode: product.barcode,
				quantity: 1,
				unitPrice: product.sellingPrice || 0,
				discountPercentage: 0,
				discountAmount: 0,
				taxPercentage: Number(product.taxRate) || 0,
				taxAmount: 0,
				totalAmount: product.sellingPrice || 0,
			});
			setSelectedProducts(prev => [...prev, newItem]);
		}

		setSearchQuery("");
		setFilteredProducts(products);
	};

	const updateItem = (itemId: string, field: keyof SalesItem, value: any) => {
		setSelectedProducts(prev =>
			prev.map(item => (item.id === itemId ? recalcItem({ ...item, [field]: value }) : item)),
		);
	};

	const removeItem = (itemId: string) => {
		setSelectedProducts(prev => prev.filter(item => item.id !== itemId));
	};

	const calculateTotals = () => {
		const subtotal = selectedProducts.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
		const totalDiscount = selectedProducts.reduce((sum, item) => sum + item.discountAmount, 0);
		const totalTax = selectedProducts.reduce((sum, item) => sum + item.taxAmount, 0);
		const total = subtotal - totalDiscount + totalTax + salesForm.shippingCost;

		return { subtotal, totalDiscount, totalTax, total };
	};

	const mapSaleStatus = (status: SalesForm["status"]): SaleUpdatePayload["status"] => {
		if (status === "ordered") return "confirmed";
		if (status === "received") return "delivered";
		return "pending";
	};

	const mapPaymentStatus = (status: SalesForm["paymentStatus"]): SaleUpdatePayload["paymentStatus"] => {
		if (status === "unpaid") return "pending";
		if (status === "partial") return "partial";
		return "paid";
	};

	const handleSubmit = async () => {
		const errors: Record<string, string> = {};
		if (!salesForm.customerId) errors.customerId = "invoices.errors.customerRequired";
		if (selectedProducts.length === 0) errors.items = "invoices.errors.productsRequired";
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			toast({
				title: t("common.error"),
				description: t("validation.checkFields"),
				variant: "destructive",
			});
			return;
		}
		setFieldErrors({});

		try {
			setSaving(true);
			const totals = calculateTotals();
			const saleData = {
				customerId: salesForm.customerId ? Number(salesForm.customerId) : undefined,
				status: mapSaleStatus(salesForm.status),
				paymentStatus: mapPaymentStatus(salesForm.paymentStatus),
				subtotal: totals.subtotal,
				discountAmount: totals.totalDiscount,
				taxAmount: totals.totalTax,
				shippingAmount: Number(salesForm.shippingCost) || 0,
				totalAmount: totals.total,
				notes: salesForm.notes || undefined,
				items: selectedProducts.map(item => ({
					productId: Number(item.productId),
					quantity: Number(item.quantity) || 0,
					unitPrice: Number(item.unitPrice) || 0,
					discountRate: Number(item.discountPercentage) || 0,
					taxRate: Number(item.taxPercentage) || 0,
				})),
			} satisfies SaleUpdatePayload;

			await salesAPI.update(id!, saleData);
			toast({
				title: t("common.success"),
				description: t("invoices.messages.updated"),
			});
			allowNavigation();
			navigate(`/invoices/${id}`);
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const { subtotal, totalDiscount, totalTax, total } = calculateTotals();

	if (loading) return <Loading />;

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("invoices.edit.title")}</PageHeading>
						<p className="text-muted-foreground">{t("invoices.edit.description")}</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button variant="outline" onClick={() => navigate(`/invoices/${id}`)} disabled={saving}>
						{t("common.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={saving}>
						{saving ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
						) : (
							<Save className="h-4 w-4 mx-2" />
						)}
						{t("common.save")}
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("invoices.edit.operationData")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>
										{t("invoices.edit.customer")} <span className="text-destructive">*</span>
									</Label>
									<Select
										value={salesForm.customerId}
										onValueChange={value => handleInputChange("customerId", value)}
									>
										<SelectTrigger
											aria-label={t("invoices.edit.customer")}
											aria-invalid={!!fieldErrors.customerId}
										>
											<SelectValue placeholder={t("invoices.edit.selectCustomer")} />
										</SelectTrigger>
										<SelectContent>
											{customers.map(customer => (
												<SelectItem key={customer.id} value={String(customer.id)}>
													{customer.firstName} {customer.lastName}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldError message={fieldErrors.customerId} />
								</div>
								<div className="space-y-2">
									<Label>{t("invoices.edit.invoiceDate")}</Label>
									<Input
										aria-label={t("invoices.edit.invoiceDate")}
										type="date"
										value={salesForm.saleDate}
										onChange={e => handleInputChange("saleDate", e.target.value)}
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>{t("invoices.edit.status")}</Label>
									<Select
										value={salesForm.status}
										onValueChange={value => handleInputChange("status", value)}
									>
										<SelectTrigger aria-label={t("invoices.edit.status")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="pending">{t("invoices.status.pending")}</SelectItem>
											<SelectItem value="ordered">{t("invoices.status.pending")}</SelectItem>
											<SelectItem value="received">{t("invoices.status.paid")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>{t("invoices.edit.paymentStatus")}</Label>
									<Select
										value={salesForm.paymentStatus}
										onValueChange={value => handleInputChange("paymentStatus", value)}
									>
										<SelectTrigger aria-label={t("invoices.edit.paymentStatus")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="unpaid">{t("invoices.status.pending")}</SelectItem>
											<SelectItem value="partial">{t("invoices.status.pending")}</SelectItem>
											<SelectItem value="paid">{t("invoices.status.paid")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							<div className="space-y-2">
								<Label>{t("invoices.edit.notes")}</Label>
								<Input
									aria-label={t("invoices.edit.notes")}
									type="text"
									value={salesForm.notes}
									onChange={e => handleInputChange("notes", e.target.value)}
									placeholder={t("invoices.edit.notesPlaceholder")}
								/>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-base">
								<Package className="h-5 w-5" />
								{t("invoices.edit.products")}
							</CardTitle>
							<CardDescription>{t("invoices.edit.productsDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="space-y-2">
								<Label>{t("invoices.edit.searchProducts")}</Label>
								<div className="relative">
									<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
									<Input
										aria-label={t("invoices.edit.searchProducts")}
										placeholder={t("invoices.edit.searchPlaceholder")}
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										className="ps-10"
									/>
								</div>
							</div>

							{searchQuery && (
								<div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{filteredProducts.map(product => (
											<div
												key={product.id}
												className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
												onClick={() => addProductToSale(product)}
											>
												<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
													{product.image ? (
														<img
															src={product.image}
															alt={product.name}
															className="w-full h-full object-cover rounded-lg"
														/>
													) : (
														<Package className="h-6 w-6 text-primary" />
													)}
												</div>
												<div className="flex-1">
													<h4 className="font-medium">{product.name}</h4>
													<p className="text-sm font-medium">
														<Price value={product.sellingPrice || 0} currency={currency} />
													</p>
												</div>
												<Button
													title={t("common.add")}
													aria-label={t("common.add")}
													size="sm"
													variant="outline"
												>
													<Plus className="h-4 w-4" />
												</Button>
											</div>
										))}
									</div>
								</div>
							)}

							<div className="space-y-4">
								<h4 className="font-medium">
									{t("invoices.edit.selectedProducts")} <span className="text-destructive">*</span>
								</h4>
								<FieldError message={fieldErrors.items} />
								{selectedProducts.map(item => (
									<div
										key={item.id}
										className="flex flex-col items-stretch gap-4 p-4 border rounded-lg md:flex-row md:items-center"
									>
										<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
											{item.productImage ? (
												<img
													src={item.productImage}
													alt={item.productName}
													className="w-full h-full object-cover rounded-lg"
												/>
											) : (
												<Package className="h-8 w-8 text-primary" />
											)}
										</div>
										<div className="flex-1">
											<h4 className="font-medium">{item.productName}</h4>
											<div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mt-2">
												<div>
													<Label className="text-sm">{t("invoices.edit.quantity")}</Label>
													<Input
														aria-label={t("invoices.edit.quantity")}
														type="number"
														min="1"
														value={item.quantity}
														onChange={e =>
															updateItem(
																item.id,
																"quantity",
																parseInt(e.target.value) || 1,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("invoices.edit.unitPrice")}</Label>
													<Input
														aria-label={t("invoices.edit.unitPrice")}
														type="number"
														min="0"
														step="0.01"
														value={item.unitPrice}
														onChange={e =>
															updateItem(
																item.id,
																"unitPrice",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-24"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("invoices.edit.discount")} </Label>
													<Input
														aria-label={t("invoices.edit.discount")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.discountPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"discountPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("invoices.edit.total")}</Label>
													<div className="text-lg font-semibold">
														<Price value={item.totalAmount} currency={currency} />
													</div>
												</div>
											</div>
										</div>
										<Button
											title={t("common.delete")}
											aria-label={t("common.delete")}
											size="sm"
											variant="destructive"
											onClick={() => removeItem(item.id)}
										>
											<Trash2 className="h-4 w-4 text-white" />
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("invoices.edit.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex justify-between">
								<span>{t("invoices.edit.subtotal")}</span>
								<span className="font-semibold">
									<Price value={subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("invoices.edit.discount")}</span>
								<span className="font-semibold text-destructive">
									-<Price value={totalDiscount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("invoices.edit.tax")}</span>
								<span className="font-semibold">
									<Price value={totalTax} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("invoices.edit.total")}</span>
									<Price value={Number(total) || 0} currency={currency} />
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default InvoiceEditPage;
